import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, createContext, useContext, useEffect } from 'react';

function ChevronDownSvg(props) {
    return (jsx("svg", Object.assign({}, props, { width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, { children: jsx("path", { d: 'M4 6L8 10L12 6', stroke: 'currentColor', strokeWidth: '1', strokeLinecap: 'round', strokeLinejoin: 'round' }) })));
}

function Copy({ text }) {
    const [copied, setCopied] = useState(false);
    const copy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };
    return copied ? (jsxs("svg", Object.assign({ className: 'json-view--copy', style: { display: 'inline-block' }, xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24' }, { children: [jsx("path", { stroke: '#A1E3CB', d: 'm10.933 13.519-2.226-2.226-1.414 1.414 3.774 3.774 5.702-6.84-1.538-1.282z' }), jsx("path", { d: 'M19 3H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2zM5 19V5h14l.002 14H5z' })] }))) : (jsx("svg", Object.assign({ onClick: copy, className: 'json-view--copy', xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24' }, { children: jsx("path", { d: 'M20 2H10c-1.103 0-2 .897-2 2v4H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2v-4h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM4 20V10h10l.002 10H4zm16-6h-4v-4c0-1.103-.897-2-2-2h-4V4h10v10z' }) })));
}

const JsonViewContext = createContext({
    collapseStringsAfterLength: 99,
    collapseObjectsAfterLength: 20,
    enableClipboard: true,
    collapsed: false
});
function JsonView({ src, collapseStringsAfterLength = 99, collapseObjectsAfterLength = 20, enableClipboard = true, collapsed = false }) {
    return (jsx(JsonViewContext.Provider, Object.assign({ value: { collapseStringsAfterLength, collapseObjectsAfterLength, enableClipboard, collapsed } }, { children: jsx("code", Object.assign({ className: 'json-view' }, { children: jsx(JsonNode, { node: src, depth: 1 }) })) })));
}
function JsonNode({ node, depth }) {
    const jv = useContext(JsonViewContext);
    if (Array.isArray(node) || isObject(node)) {
        return jsx(ObjectNode, { node: node, depth: depth });
    }
    else if (typeof node === 'string')
        return (jsxs(Fragment, { children: [node.length > jv.collapseStringsAfterLength ? (jsx(LongString, { str: node })) : (jsxs("span", Object.assign({ className: 'json-view--string' }, { children: ["\"", node, "\""] }))), jv.enableClipboard && jsx(Copy, { text: node })] }));
    else {
        const type = typeof node;
        const value = String(node);
        return (jsxs(Fragment, { children: [type === 'number' || type === 'bigint' ? (jsx("span", Object.assign({ className: 'json-view--number' }, { children: value }))) : type === 'boolean' ? (jsx("span", Object.assign({ className: 'json-view--boolean' }, { children: value }))) : node === null ? (jsx("span", Object.assign({ className: 'json-view--null' }, { children: "null" }))) : (jsx("span", Object.assign({ className: 'json-view--string' }, { children: value }))), jv.enableClipboard && jsx(Copy, { text: value })] }));
    }
}
function ObjectNode({ node, depth }) {
    const jv = useContext(JsonViewContext);
    const [fold, setFold] = useState(jv.collapsed === true ||
        (typeof jv.collapsed === 'number' && depth > jv.collapsed) ||
        (Array.isArray(node) && node.length > jv.collapseObjectsAfterLength) ||
        (isObject(node) && Object.keys(node).length > jv.collapseObjectsAfterLength)
        ? true
        : false);
    useEffect(() => {
        const originCollapsed = (Array.isArray(node) && node.length > jv.collapseObjectsAfterLength) ||
            (isObject(node) && Object.keys(node).length > jv.collapseObjectsAfterLength);
        if (typeof jv.collapsed === 'boolean') {
            setFold(jv.collapsed || originCollapsed);
        }
        else if (typeof jv.collapsed === 'number') {
            setFold(depth > jv.collapsed || originCollapsed);
        }
    }, [jv.collapsed, jv.collapseObjectsAfterLength, jv.collapseObjectsAfterLength]);
    if (Array.isArray(node)) {
        return (jsxs(Fragment, { children: [jsx("span", { children: '[' }), !fold && jsx(ChevronDownSvg, { onClick: () => setFold(true), className: 'jv-chevron' }), !fold && jv.enableClipboard && jsx(Copy, { text: JSON.stringify(node) }), !fold ? (jsx("div", Object.assign({ className: 'jv-indent' }, { children: node.map((n, i) => (jsx(NameValue, { name: i, value: n, depth: depth }, i))) }))) : (jsx("button", Object.assign({ onClick: () => setFold(false), className: 'jv-button' }, { children: "..." }))), jsx("span", { children: ']' })] }));
    }
    else if (isObject(node)) {
        return (jsxs(Fragment, { children: [jsx("span", { children: '{' }), !fold && jsx(ChevronDownSvg, { onClick: () => setFold(true), className: 'jv-chevron' }), !fold && jv.enableClipboard && jsx(Copy, { text: JSON.stringify(node) }), !fold ? (jsx("div", Object.assign({ className: 'jv-indent' }, { children: Object.entries(node).map(([name, value]) => (jsx(NameValue, { name: name, value: value, depth: depth }, name))) }))) : (jsx("button", Object.assign({ onClick: () => setFold(false), className: 'jv-button' }, { children: "..." }))), jsx("span", { children: '}' })] }));
    }
    return null;
}
function LongString({ str }) {
    if (str.length <= 10)
        return jsxs("span", Object.assign({ className: 'json-view--string' }, { children: ["\"", str, "\""] }));
    const [fold, setFold] = useState(true);
    return (jsxs("span", Object.assign({ onClick: () => setFold(!fold), className: 'json-view--string cursor-pointer' }, { children: ["\"", fold ? str.slice(0, 6) + '...' + str.slice(-4) : str, "\""] })));
}
function NameValue({ name, value, depth }) {
    return (jsxs("div", Object.assign({ className: 'json-view--pair' }, { children: [jsx("span", Object.assign({ className: typeof name === 'number' ? 'json-view--index' : 'json-view--property' }, { children: name })), ":", ' ', jsx(JsonNode, { node: value, depth: depth + 1 })] })));
}
function isObject(node) {
    return Object.prototype.toString.call(node) === '[object Object]';
}

export { JsonView as default };
//# sourceMappingURL=index.mjs.map
